import React, { useCallback, useEffect, useMemo, useState } from "react";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box } from "@mui/material";
import { AssetClassCell } from "components/DealGroupedTable/AssetClassCell";
import { CharacteristicCell } from "components/DealGroupedTable/CharacteristicCell";
import {
  ASSET_CLASS_KEY,
  DEAL_NAME_COLUMN_KEY,
  isCharacteristicColumn,
  resolveColumnsByTitle,
  ResolvedColumn,
  SUB_ASSET_CLASS_KEY
} from "components/DealGroupedTable/dealGroupedTable.config";
import {
  cellStr,
  ColumnHeader,
  DealNameCell,
  DealRowData,
  ValueCell
} from "components/DealGroupedTable/DealTableCells";
import dayjs from "dayjs";
import { useActionHook } from "hooks";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnOrderState,
  MRT_Row,
  MRT_SortingState,
  useMaterialReactTable
} from "material-react-table";
import { useDateFormatSelect, useDealActions, useDealQueriesSelect, useUserRoleSelector } from "store";
import { DealAssetSimple, DealBarCharacteristic, DealsTableCell } from "store/types";
import { isRoleAnalyst } from "utils/user";

import { useGroupingView } from "../GroupingViewContext";

/** Characteristic data types with no inline support in the deal-list — their cells are hidden entirely. */
const HIDDEN_CHARACTERISTIC_DATA_TYPES: string[] = ["file", "list"];

/** ISO date (`2026-05-07`) or datetime (`2026-05-07T08:31:21.000000Z`) — these get formatted for display. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/** Format a plain-string cell: ISO dates use the account format, everything else is shown verbatim. */
const formatPlainCell = (value: DealsTableCell | undefined, dateFormat: string): string | null => {
  const str = cellStr(value);
  if (str && ISO_DATE_RE.test(str)) {
    const parsed = dayjs(str);
    if (parsed.isValid()) return parsed.format(dateFormat);
  }
  return str;
};

/** Stable self-id for a row: deal id for leaves, group title (index fallback) for groups. */
const rowSelfId = (row: DealRowData, index: number): string =>
  row.id != null ? String(row.id) : cellStr(row["group_title"]) || `g${index}`;

/** All expandable (group) row ids in the tree — matches the table's getRowId scheme. */
const collectGroupKeys = (rows: DealRowData[], prefix = ""): string[] => {
  const keys: string[] = [];
  rows.forEach((row, index) => {
    const id = prefix + rowSelfId(row, index);
    if (row.children?.length) {
      keys.push(id);
      keys.push(...collectGroupKeys(row.children, `${id}/`));
    }
  });
  return keys;
};

export const DealListTable: React.FC = () => {
  const queries = useDealQueriesSelect();
  const {
    getDealsTableActions,
    getDealsTableColumnsActions,
    getAssetClassesAction,
    updateQueries,
    updateDealListViewConfigAction
  } = useDealActions();
  const { isCollapsed, toggleGroup, registerGroupKeys } = useGroupingView();

  // Only admins and team members (RoleName.Analyst) may edit cells; others see read-only values.
  const role = useUserRoleSelector();
  const canEdit = isRoleAnalyst({ role, preventTV: true });
  const dateFormat = useDateFormatSelect();

  const { action, data, loading, setData } = useActionHook(getDealsTableActions);
  const { action: getDealsTableColumns } = useActionHook(getDealsTableColumnsActions);
  const { data: assetClasses, action: loadAssetClasses } = useActionHook(getAssetClassesAction);

  useEffect(() => {
    loadAssetClasses();
  }, []);

  const [allColumnDefs, setAllColumnDefs] = useState<{ key: string; title: string }[] | null>(null);

  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const getColumns = async () => {
    const columns = await getDealsTableColumns();
    setAllColumnDefs(columns);
  };

  useEffect(() => {
    getColumns();
  }, []);

  const dealListCharacteristics = queries.dealListCharacteristics;
  const visibleColumns = useMemo<ResolvedColumn[]>(() => {
    if (!allColumnDefs) return [];
    return resolveColumnsByTitle(dealListCharacteristics, allColumnDefs);
  }, [dealListCharacteristics, allColumnDefs]);

  const columnOrder = useMemo(
    () => [DEAL_NAME_COLUMN_KEY, ...visibleColumns.map((column) => column.key)],
    [visibleColumns]
  );

  const dealListCharacteristicsOptions = queries.dealListCharacteristicsOptions;
  const dealListGroupBy = queries.dealListGroupBy;

  // Persist a reordered column set into the live queries AND the server-side dealListViewConfig blob.
  const handleColumnOrderChange = useCallback(
    (updater: MRT_ColumnOrderState | ((prev: MRT_ColumnOrderState) => MRT_ColumnOrderState)) => {
      const nextOrder = typeof updater === "function" ? updater(columnOrder) : updater;
      const keyToTitle = new Map(visibleColumns.map((column) => [column.key, column.title]));

      // Rebuild the checked-title list in the new visual order; keep the deal-name column pinned first
      // (excluded from the reorder) and preserve any checked titles that don't resolve to a column.
      const orderedTitles = nextOrder
        .map((key) => keyToTitle.get(key))
        .filter((title): title is string => title !== undefined);
      const orderedSet = new Set(orderedTitles);
      const remainingTitles = dealListCharacteristics.filter((title) => !orderedSet.has(title));
      const nextCharacteristics = [...orderedTitles, ...remainingTitles];

      if (nextCharacteristics.join(",") === dealListCharacteristics.join(",")) return; // no real change

      updateQueries({ dealListCharacteristics: nextCharacteristics });
      updateDealListViewConfigAction({
        dealListGroupBy,
        dealListCharacteristics: nextCharacteristics,
        dealListCharacteristicsOptions
      }).catch(() => undefined); // best-effort; live state already updated
    },
    [
      columnOrder,
      visibleColumns,
      dealListCharacteristics,
      dealListCharacteristicsOptions,
      dealListGroupBy,
      updateQueries,
      updateDealListViewConfigAction
    ]
  );

  const deals_group_by = dealListGroupBy;
  const groupsKey = deals_group_by.join(",");
  // Keyed on the column SET (sorted), not its order — reordering is purely visual (rows are keyed by
  // column key), so a drag-reorder must not trigger a data refetch; only add/remove should.
  const columnsKey = [...columnOrder].sort().join(",");

  useEffect(() => {
    if (!allColumnDefs) return; // wait until the supported columns are resolved
    action({ ...queries, columns: columnOrder, deals_group_by, view: "table" });
  }, [columnsKey, groupsKey, allColumnDefs, queries.deal_title, queries.filters, queries.order_by]);

  const groupKeys = useMemo(() => collectGroupKeys(data?.rows ?? []), [data]);

  useEffect(() => registerGroupKeys(groupKeys), [groupKeys, registerGroupKeys]);
  const expanded = useMemo<Record<string, boolean>>(
    () => Object.fromEntries(groupKeys.map((key) => [key, !isCollapsed(key)])),
    [groupKeys, isCollapsed]
  );

  const patchCell = useCallback(
    (rowId: number, columnKey: string, updated: DealsTableCell) => {
      setData((prev) => {
        if (!prev) return prev;
        const patchRows = (rows: DealRowData[]): DealRowData[] =>
          rows.map((row): DealRowData => {
            if (row.id === rowId) return { ...row, [columnKey]: updated } as DealRowData;
            if (row.children) return { ...row, children: patchRows(row.children) } as DealRowData;
            return row;
          });
        return { ...prev, rows: patchRows(prev.rows) };
      });
    },
    [setData]
  );

  const columns = useMemo<MRT_ColumnDef<DealRowData>[]>(() => {
    const labelKeys = columnOrder;

    // Cell renderer for a single column, decided by its key:
    //  - asset / sub-asset      → bespoke Asset editor
    //  - characteristic columns → characteristic editor (Currency + `characteristic___*` keys)
    //  - anything else          → read-only text (ISO dates are formatted).
    const renderCell = (column: ResolvedColumn, row: MRT_Row<DealRowData>) => {
      if (row.getCanExpand()) return null;
      const dealId = row.original.id;
      const value = row.original[column.key];

      if (column.key === ASSET_CLASS_KEY) {
        return (
          <AssetClassCell
            mode="asset"
            value={value as DealAssetSimple | null}
            dealId={dealId}
            assetClasses={assetClasses ?? []}
            canEdit={canEdit}
            onSuccess={(updated) => {
              patchCell(dealId, column.key, updated);
              patchCell(dealId, SUB_ASSET_CLASS_KEY, null);
            }}
          />
        );
      }

      if (column.key === SUB_ASSET_CLASS_KEY) {
        return (
          <AssetClassCell
            mode="subAsset"
            value={value as DealAssetSimple | null}
            dealId={dealId}
            parentAsset={row.original[ASSET_CLASS_KEY] as DealAssetSimple | null}
            assetClasses={assetClasses ?? []}
            canEdit={canEdit}
            onSuccess={(updated) => patchCell(dealId, column.key, updated)}
          />
        );
      }

      if (isCharacteristicColumn(column.key)) {
        const characteristic = value as DealBarCharacteristic | null;
        // File / list characteristics aren't supported inline — don't display them at all.
        if (characteristic && HIDDEN_CHARACTERISTIC_DATA_TYPES.includes(characteristic.data_type)) {
          return null;
        }
        return (
          <CharacteristicCell
            value={characteristic}
            dealId={dealId}
            variant={column.variant}
            canEdit={canEdit}
            onSuccess={(updated) => patchCell(dealId, column.key, updated)}
          />
        );
      }

      // Non-characteristic scalar column (e.g. dates, statuses): read-only, never editable.
      return <ValueCell value={formatPlainCell(value, dateFormat)} variant={column.variant} />;
    };

    return [
      {
        id: DEAL_NAME_COLUMN_KEY,
        header: "Deal Name",
        Header: () => <ColumnHeader title="Deal Name" />,
        accessorFn: (row) => cellStr(row["group_title"]) ?? cellStr(row[DEAL_NAME_COLUMN_KEY]),
        grow: true,
        size: 320,
        minSize: 220,
        // Deal Name stays pinned first — it is not part of the reorderable set.
        enableColumnOrdering: false,
        Cell: ({ row }) => <DealNameCell row={row} labelKeys={labelKeys} onToggle={toggleGroup} />
      },
      ...visibleColumns.map(
        (column): MRT_ColumnDef<DealRowData> => ({
          id: column.key,
          header: column.title,
          Header: () => <ColumnHeader title={column.title} />,
          accessorFn: (row) => cellStr(row[column.key]),
          grow: true,
          size: 170,
          minSize: 120,
          Cell: ({ row }) => renderCell(column, row)
        })
      )
    ];
  }, [visibleColumns, columnOrder, patchCell, toggleGroup, assetClasses, canEdit, dateFormat]);

  const table = useMaterialReactTable<DealRowData>({
    columns,
    data: data?.rows ?? [],
    layoutMode: "grid",
    enableColumnResizing: true,
    columnResizeMode: "onEnd",
    getRowId: (row, index, parent) => `${parent ? `${parent.id}/` : ""}${rowSelfId(row, index)}`,

    // -------- Performance (10k+ rows) --------
    enableRowVirtualization: true,
    rowVirtualizerOptions,

    // -------- Tree / expansion --------
    enableExpanding: true,
    getSubRows: (row) => row.children,

    // -------- Sorting (client-side) --------
    enableSorting: true,
    enableMultiSort: false,
    onSortingChange: setSorting,

    // -------- Column ordering (drag to reorder, persisted to dealListViewConfig) --------
    enableColumnOrdering: true,
    onColumnOrderChange: handleColumnOrderChange,
    icons: tableIcons,

    // -------- Disabled chrome --------
    enablePagination: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableTableFooter: false,
    enableStickyHeader: true,

    // -------- State / styling --------
    state: { isLoading: loading || allColumnDefs === null, columnOrder, expanded, sorting },
    // Expansion is controlled via the grouping context; toggles happen through it.
    onExpandedChange: () => undefined,
    displayColumnDefOptions,
    muiTablePaperProps: tablePaperProps,
    muiTableProps: tableProps,
    muiTableContainerProps: tableContainerProps,
    muiTableHeadCellProps: tableHeadCellProps,
    muiTableBodyCellProps: tableBodyCellProps,
    muiTableBodyRowProps: tableBodyRowProps
  });

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <MaterialReactTable table={table} />
    </Box>
  );
};

// ------------------------------ Styling ------------------------------------

const tablePaperProps = {
  sx: {
    borderRadius: "8px",
    boxShadow: "0 1px 2px 0 #1212171a",
    border: "1px solid #dbe8f5",
    height: "100%",
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column"
  }
} as const;

const tableProps = { sx: { width: "100%" } } as const;
const tableContainerProps = { sx: { width: "100%", flex: "1 1 auto", minHeight: 0, maxHeight: "100%" } } as const;

const tableHeadCellProps = {
  sx: {
    bgcolor: "#f6f8fc",
    color: "#071f35",
    fontWeight: 500,
    fontSize: "14px",
    borderBottom: "1px solid #dbe8f5",
    padding: "8px 20px",
    "& .Mui-TableHeadCell-ResizeHandle-Wrapper": { display: "none" }
  }
} as const;

const tableBodyCellProps = {
  sx: {
    bgcolor: "#fff",
    borderBottom: "1px solid #dbe8f5",
    padding: "0 20px",
    height: 48,
    fontSize: "15px",
    color: "#071f35"
  }
} as const;

const tableBodyRowProps = { sx: { "&:hover td": { backgroundColor: "#ecf1f8" } } } as const;

const rowVirtualizerOptions = { estimateSize: () => 48, overscan: 8 } as const;

// Column-reorder grab handle — same icon as the workspace MetadataTable.
const tableIcons = { DragHandleIcon: () => <DragIndicatorIcon sx={{ fontSize: 16, color: "#92a7bc" }} /> };

const displayColumnDefOptions = {
  "mrt-row-expand": {
    size: 0,
    minSize: 0,
    grow: false,
    Header: () => null,
    Cell: () => null,
    muiTableHeadCellProps: { sx: { display: "none" } },
    muiTableBodyCellProps: { sx: { display: "none" } }
  }
} as const;
